import { FC, useState, useRef, useEffect, createContext, useContext } from 'react'
import styles from 'components/atoms/inputs/Select/FitToHeight/FitToHeight.styl'
import cn from 'classnames'
import { cssVar } from 'helpers/styleHelpers'

const DEFAULT_GAP = 50

const DEFAULT_CONTEXT = {
  viewportNode: null,
}
type FitToHeightContextType = {
  viewportNode: HTMLElement | null
}
const FitToHeightContext = createContext<FitToHeightContextType>(DEFAULT_CONTEXT)


const useViewport = () => {
  const { viewportNode } = useContext(FitToHeightContext)
  if (viewportNode === null) {
    return {
      spaceAbove: 0,
      spaceBelow: 0,
    }
  }

  const { innerHeight } = window
  const { top, bottom } = viewportNode.getBoundingClientRect()
  const spaceBelow = innerHeight - bottom
  return {
    spaceAbove: top,
    spaceBelow,
  }
}
type UseViewportType = ReturnType<typeof useViewport>


type FitToHeightProps = {
}
export const FitToHeight: FC<FitToHeightProps> = (props) => {
  const { children } = props

  const [{ isInited, height, isBellow }, setState] = useState({
    isInited: false,
    height: 0,
    isBellow: true,
  })

  const viewportContext = useViewport()
  const viewportRef = useRef<UseViewportType>()
  viewportRef.current = viewportContext

  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const { current } = rootRef
    const { current: viewport } = viewportRef
    if (!current || !viewport) {
      return
    }

    const { innerHeight } = window
    const { top, bottom } = current.getBoundingClientRect()

    const spaceAbove = top - viewport.spaceAbove - DEFAULT_GAP
    const spaceBelow = innerHeight - bottom - viewport.spaceBelow - DEFAULT_GAP
    if (spaceBelow > spaceAbove) {
      setState({
        isInited: true,
        height: spaceBelow,
        isBellow: true,
      })
    } else {
      setState({
        isInited: true,
        height: spaceAbove,
        isBellow: false,
      })
    }
  }, [])

  return (
    <div
      ref={ rootRef }
      className={ cn(styles.fitToHeight, isBellow && styles.bellow, !isBellow && styles.above) }
      style={ cssVar({
        '--height': `${ height }px`,
      }) }
    >
      { isInited && children }
    </div>
  )
}

type FitToHeightViewportProps = {}
export const FitToHeightViewport: FC<FitToHeightViewportProps> = (props) => {
  const { children } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const [context, setContext] = useState<FitToHeightContextType>()

  useEffect(() => {
    const { current } = rootRef
    if (!current) {
      return
    }

    setContext({
      viewportNode: current,
    })

    return () => {
      setContext({
        viewportNode: null,
      })
    }
  }, [])

  return (
    <div ref={ rootRef } className={ styles.viewport }>
      { context && (
        <FitToHeightContext.Provider value={ context }>
          { children }
        </FitToHeightContext.Provider>
      )}
    </div>
  )
}
